import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import appLogger from '@/utils/logger';

const HEADER_KEYS = [
  'x-request-id',
  'x-correlation-id',
  'request-id',
  'x-trace-id',
] as const;

function readHeader(
  headers: AxiosResponse['headers'] | undefined,
  name: string
): string | undefined {
  if (headers == null) return undefined;
  const lower = name.toLowerCase();
  const get = (headers as { get?: (key: string) => unknown }).get;
  if (typeof get === 'function') {
    const v = get(name) ?? get(lower);
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const rec = headers as Record<string, unknown>;
  for (const [k, v] of Object.entries(rec)) {
    if (k.toLowerCase() === lower) {
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (Array.isArray(v) && v[0] != null) return String(v[0]).trim();
    }
  }
  return undefined;
}

/**
 * Returns a request/correlation/trace id from common API response headers (no PII).
 */
export function getCorrelationIdFromHeaders(
  headers: AxiosResponse['headers'] | undefined
): string | undefined {
  if (!headers) return undefined;
  for (const key of HEADER_KEYS) {
    const v = readHeader(headers, key);
    if (v) return v;
  }
  return undefined;
}

/**
 * On 429 or 5xx, logs correlation id when the response includes a known header (support/debug; no PII).
 */
export function logCorrelationIdForRateLimitOrServerErrors(
  error: unknown,
  context: string
): void {
  if (typeof axios.isAxiosError !== 'function' || !axios.isAxiosError(error)) {
    return;
  }
  const status = error.response?.status;
  if (status !== 429 && (status == null || status < 500)) {
    return;
  }
  const correlationId = getCorrelationIdFromHeaders(error.response?.headers);
  if (!correlationId) return;
  const method = (error.config?.method ?? 'GET').toUpperCase();
  const path = error.config?.url ?? '';
  appLogger.warn(`${context}: HTTP ${status} correlationId=${correlationId}`, {
    method,
    path,
  });
}

/**
 * Register last on the axios instance so this runs first on response errors (axios runs error interceptors LIFO).
 */
export function attachCorrelationErrorLogging(
  api: AxiosInstance,
  context: string
): void {
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      logCorrelationIdForRateLimitOrServerErrors(error, context);
      return Promise.reject(error);
    }
  );
}
